import { Link } from '@tanstack/react-router';
import { Package, PauseCircle } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { useRunCurationOutcomes } from '../hooks/use-run-curation';
import { useRunProgress } from '../hooks/use-run-progress';
import type { RunProgress } from '../types/run.api.types';
import { catalogLabel } from '../utils/catalog-labels';
import { runOutcomeSearch } from '../utils/run-product-drill';

import { Callout } from './callout';
import { DeliveryFieldSets } from './delivery-field-sets';
import { DeliveryStatusPanel } from './delivery-status-panel';
import { HelpTerms } from './page-intro';
import { PaneActionButton } from './pane-action-button';

/**
 * The Publish help terms (ready to ship, held out, what ships, what approving does) — moved from
 * the old Publishing tab into Summary's "How it works" panel when that tab folded into Summary.
 */
export function DeliveryHelp() {
  const { t } = useTranslation('catalog-enrichment-run');
  return (
    <HelpTerms
      terms={[
        { term: t('approval.help.readyTerm'), description: t('approval.help.ready') },
        { term: t('approval.heldOutLabel'), description: t('approval.heldOutBody') },
        { term: t('fieldSets.title'), description: t('approval.help.whatShips') },
        { term: t('decision.approveAndDeliver'), description: t('gate.nextCopy.APPROVAL') },
      ]}
    />
  );
}

/**
 * "N products held out" — matched products flagged as needing attention, so neither enriched nor
 * shipped — with its two drill-downs (this batch's Flagged list, then the catalog-wide view).
 * Renders nothing when none are held out. Same read the old Publishing tab made.
 */
export function ApprovalHeldOutCallout({ runId }: { runId: string }) {
  const { t } = useTranslation('catalog-enrichment-run');
  const progress = useRunProgress(runId).data;
  const hasCuration = !!progress?.stages.some((s) => s.stage === 'CURATION');
  const outcomes = useRunCurationOutcomes(runId, hasCuration);
  const heldOut = outcomes.data?.needsAttention ?? 0;
  if (heldOut <= 0) return null;
  return (
    <Callout
      tone="warning"
      title={t('approval.heldOut', { count: heldOut })}
      action={
        // Design B — the batch's own list first (Products, Flagged), then the catalog-wide view.
        <span className="flex flex-wrap items-center gap-2">
          <PaneActionButton icon={PauseCircle}>
            <Link
              to="/catalog-enrichment/runs/$runId/products"
              params={{ runId }}
              search={{ attention: 'flagged' }}
            >
              {t('gate.heldBackInProducts')}
            </Link>
          </PaneActionButton>
          <PaneActionButton icon={Package}>
            <Link
              to="/catalog-enrichment/products"
              search={runOutcomeSearch(runId, 'NEEDS_ATTENTION')}
            >
              {t('approval.heldOutLink')}
            </Link>
          </PaneActionButton>
        </span>
      }
    >
      {t('approval.heldOutBody')}
    </Callout>
  );
}

interface PublishResultsProps {
  runId: string;
  progress: RunProgress;
  /** `false` when the Publish pane's metric strip already shows delivered / failed. */
  showCounts?: boolean;
  /** Inside a stage-pane section (no card chrome of its own). */
  embedded?: boolean;
}

/**
 * "Did it land?" — the delivery status (last error, suppression, unmatched callback items, job
 * id, per-product results, rejections), moved here from the old Publishing tab's after-delivery
 * state.
 */
export function PublishResults({
  runId,
  progress,
  showCounts = true,
  embedded = false,
}: PublishResultsProps) {
  const { t } = useTranslation('catalog-enrichment-run');
  const { t: tCatalog } = useTranslation('catalog-enrichment');
  const connector = progress.connectorCode
    ? catalogLabel(tCatalog, 'connector', progress.connectorCode)
    : null;
  // Before delivery only what's true: one sentence, no tables, no buttons (P1).
  if (!progress.delivery) {
    return <p className="text-muted-foreground text-sm">{t('delivery.notStarted')}</p>;
  }
  // P1 (2026-10-16): no "Compare" button any more — the Compare page was retired (its table is
  // the product page's "Content generation changes"); the rejected/live lists are the only links.
  return (
    <DeliveryStatusPanel
      runId={runId}
      delivery={progress.delivery}
      destination={connector}
      showCounts={showCounts}
      embedded={embedded}
    />
  );
}

/**
 * Summary's Publishing sections for a backend WITHOUT the funnel summary (today's progress-card
 * fallback has no stage panes): held-out + "What ships" while the APPROVAL gate is open, the
 * delivery results once it has shipped — so folding the Publishing tab into Summary never loses
 * them on an older deploy.
 */
export function RunPublishingFallback({
  runId,
  progress,
}: {
  runId: string;
  progress: RunProgress;
}) {
  const { t } = useTranslation('catalog-enrichment-run');
  const { t: tCatalog } = useTranslation('catalog-enrichment');
  const gateOpen = progress.pendingGate?.stage === 'APPROVAL';
  const delivered = !!progress.delivery || progress.runStatus === 'COMPLETED';
  const connector = progress.connectorCode
    ? catalogLabel(tCatalog, 'connector', progress.connectorCode)
    : null;
  if (gateOpen) {
    return (
      <section className="bg-card flex flex-col gap-3 rounded-xl border p-4">
        <ApprovalHeldOutCallout runId={runId} />
        <DeliveryFieldSets
          runId={runId}
          title={connector ? t('approval.whatShipsTo', { connector }) : t('fieldSets.title')}
        />
      </section>
    );
  }
  if (!delivered) return null;
  return <PublishResults runId={runId} progress={progress} />;
}
